import React, { useEffect, useState } from 'react';
import {
  Card,
  Table,
  Tag,
  Typography,
  Space,
  Button,
  Input,
  Select,
  DatePicker,
  Spin,
  message,
  Modal,
  Form,
  InputNumber,
  Tooltip,
  Row,
  Col
} from 'antd';
import {
  SearchOutlined,
  DownloadOutlined,
  ReloadOutlined,
  UndoOutlined,
  EyeOutlined,
  FilterOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { billingAdminService, Transaction } from '../../services/billingAdminService';
import { downloadBlob, readBlobError } from '../../utils/fileDownload';
import { TransactionAmount } from '../../components/admin';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const TransactionList: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20
  });
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    search: '',
    startDate: '',
    endDate: ''
  });
  const [refundModal, setRefundModal] = useState<{ visible: boolean; transaction: Transaction | null }>({
    visible: false,
    transaction: null
  });
  const [refundForm] = Form.useForm();
  const { user } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.type === 'ADMIN') {
      loadTransactions();
    }
  }, [user, filters, pagination.currentPage]);

  const loadTransactions = async () => {
    try {
      setLoading(true);
      const result = await billingAdminService.getTransactions({
        page: pagination.currentPage,
        limit: pagination.itemsPerPage,
        ...filters
      });
      setTransactions(result.data);
      setPagination(result.pagination);
    } catch (error) {
      console.error('Failed to load transactions:', error);
      message.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const handleRefund = async (values: { amount?: number; reason?: string }) => {
    if (!refundModal.transaction) return;

    try {
      await billingAdminService.processRefund(refundModal.transaction.TransactionID, values);
      message.success('Refund processed successfully');
      setRefundModal({ visible: false, transaction: null });
      refundForm.resetFields();
      loadTransactions();
    } catch (error) {
      message.error('Failed to process refund');
    }
  };

  const handleExport = async () => {
    try {
      const blob = await billingAdminService.exportTransactionsCSV(filters);
      downloadBlob(blob, `transactions-${dayjs().format('YYYY-MM-DD')}.csv`);
      message.success('Export completed');
    } catch (error) {
      console.error('Failed to export transactions:', error);
      message.error(await readBlobError(error, 'Export failed'));
    }
  };

  const getStatusColor = (status: string) => {
    const statusColors: Record<string, string> = {
      'succeeded': 'green',
      'pending': 'gold',
      'failed': 'red',
      'refunded': 'purple'
    };
    return statusColors[status] || 'default';
  };

  const getTypeColor = (type: string) => {
    const typeColors: Record<string, string> = {
      'payment': 'blue',
      'refund': 'orange',
      'authorization': 'purple',
      'topup': 'cyan'
    };
    return typeColors[type] || 'default';
  };

  const columns = [
    {
      title: 'Transaction ID',
      dataIndex: 'ExternalID',
      key: 'ExternalID',
      width: 150,
      render: (text: string, record: Transaction) => (
        record.Source === 'order' ? (
          <Space direction="vertical" size={0}>
            <Text>{text}</Text>
            {record.RazorpayPaymentID && (
              <Text type="secondary" style={{ fontSize: '12px' }}>{record.RazorpayPaymentID}</Text>
            )}
          </Space>
        ) : record.Source === 'wallet' ? (
          <Text>{text}</Text>
        ) : (
          <Button type="link" size="small" onClick={() => navigate(`/admin/billing/transactions/${record.TransactionID}`)}>
            {text}
          </Button>
        )
      )
    },
    {
      title: 'Customer',
      dataIndex: 'CustomerName',
      key: 'CustomerName',
      width: 200,
      render: (name: string, record: Transaction) => (
        <Space direction="vertical" size={0}>
          <Text strong>{name || 'N/A'}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>{record.CustomerEmail}</Text>
        </Space>
      )
    },
    {
      title: 'Type',
      dataIndex: 'Type',
      key: 'Type',
      width: 100,
      render: (type: string) => (
        <Tag color={getTypeColor(type)}>{type === 'topup' ? 'TOP-UP' : type.toUpperCase()}</Tag>
      )
    },
    {
      title: 'Amount',
      dataIndex: 'Amount',
      key: 'Amount',
      width: 140,
      render: (_: number, record: Transaction) => <TransactionAmount record={record} />
    },
    {
      title: 'Status',
      dataIndex: 'Status',
      key: 'Status',
      width: 110,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{status.toUpperCase()}</Tag>
      )
    },
    {
      title: 'Payment Method',
      key: 'PaymentMethod',
      width: 150,
      responsive: ['lg'] as const,
      render: (_: any, record: Transaction) => (
        record.PaymentMethodLast4 ? (
          <Space size={4}>
            <Text>{record.PaymentMethodBrand?.toUpperCase()}</Text>
            <Text type="secondary">****{record.PaymentMethodLast4}</Text>
          </Space>
        ) : <Text type="secondary">N/A</Text>
      )
    },
    {
      title: 'Invoice',
      dataIndex: 'InvoiceRefNo',
      key: 'InvoiceRefNo',
      width: 120,
      responsive: ['md'] as const,
      render: (refNo: string) => refNo ? (
        <Button type="link" size="small" onClick={() => navigate(`/admin/billing/invoices?search=${refNo}`)}>
          {refNo}
        </Button>
      ) : <Text type="secondary">-</Text>
    },
    {
      title: 'Date',
      dataIndex: 'CreatedAt',
      key: 'CreatedAt',
      width: 160,
      responsive: ['md'] as const,
      render: (date: string) => dayjs(date).format('MMM D, YYYY HH:mm')
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: 'right' as const,
      render: (_: any, record: Transaction) => (
        <Space size="small">
          {(!record.Source || record.Source === 'transaction') && (
            <Tooltip title="View Details">
              <Button
                type="text"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => navigate(`/admin/billing/transactions/${record.TransactionID}`)}
              />
            </Tooltip>
          )}
          {/* Card orders are refunded by cancelling them on the Orders screen. */}
          {record.Status === 'succeeded' && record.Type === 'payment' && !record.IsWalletTopup && record.Source !== 'order' && (
            <Tooltip title="Refund">
              <Button
                type="text"
                size="small"
                danger
                icon={<UndoOutlined />}
                onClick={() => {
                  setRefundModal({ visible: true, transaction: record });
                  refundForm.setFieldsValue({ amount: record.Amount });
                }}
              />
            </Tooltip>
          )}
        </Space>
      )
    }
  ];

  // Note: Admin access is enforced by AuthGuard at the route level

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={2}>Transactions</Title>
          <Text type="secondary">View and manage all payment transactions</Text>
        </div>
        <Space>
          <Button icon={<DownloadOutlined />} onClick={handleExport}>
            Export CSV
          </Button>
          <Button icon={<ReloadOutlined />} onClick={loadTransactions}>
            Refresh
          </Button>
        </Space>
      </div>

      {/* Filters */}
      <Card style={{ marginBottom: '16px' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="Search by ID, customer..."
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              allowClear
            />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Select
              placeholder="Status"
              style={{ width: '100%' }}
              value={filters.status || undefined}
              onChange={(value) => setFilters({ ...filters, status: value || '' })}
              allowClear
            >
              <Option value="succeeded">Succeeded</Option>
              <Option value="pending">Pending</Option>
              <Option value="failed">Failed</Option>
              <Option value="refunded">Refunded</Option>
            </Select>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Select
              placeholder="Type"
              style={{ width: '100%' }}
              value={filters.type || undefined}
              onChange={(value) => setFilters({ ...filters, type: value || '' })}
              allowClear
            >
              <Option value="payment">Payment</Option>
              <Option value="refund">Refund</Option>
              <Option value="authorization">Authorization</Option>
              <Option value="topup">Top-up</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <RangePicker
              style={{ width: '100%' }}
              onChange={(dates) => {
                if (dates) {
                  setFilters({
                    ...filters,
                    startDate: dates[0]?.format('YYYY-MM-DD') || '',
                    endDate: dates[1]?.format('YYYY-MM-DD') || ''
                  });
                } else {
                  setFilters({ ...filters, startDate: '', endDate: '' });
                }
              }}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Button
              icon={<FilterOutlined />}
              onClick={() => {
                setFilters({ status: '', type: '', search: '', startDate: '', endDate: '' });
                setPagination({ ...pagination, currentPage: 1 });
              }}
            >
              Clear Filters
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Transactions Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={transactions}
          rowKey="TransactionID"
          loading={loading}
          pagination={{
            current: pagination.currentPage,
            total: pagination.totalItems,
            pageSize: pagination.itemsPerPage,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} transactions`,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            onChange: (page, pageSize) => {
              setPagination({
                ...pagination,
                currentPage: page,
                itemsPerPage: pageSize || 20
              });
            }
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Refund Modal */}
      <Modal
        title={`Refund Transaction: ${refundModal.transaction?.ExternalID}`}
        open={refundModal.visible}
        onCancel={() => {
          setRefundModal({ visible: false, transaction: null });
          refundForm.resetFields();
        }}
        footer={null}
      >
        <Form form={refundForm} onFinish={handleRefund} layout="vertical">
          <Form.Item label="Original Amount">
            <Text strong>${refundModal.transaction?.Amount.toFixed(2)} {refundModal.transaction?.Currency}</Text>
          </Form.Item>
          <Form.Item
            name="amount"
            label="Refund Amount"
            rules={[
              { required: true, message: 'Please enter refund amount' },
              {
                validator: (_, value) => {
                  if (value && refundModal.transaction && value > refundModal.transaction.Amount) {
                    return Promise.reject('Refund amount cannot exceed original amount');
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0.01}
              max={refundModal.transaction?.Amount}
              precision={2}
              prefix="$"
            />
          </Form.Item>
          <Form.Item name="reason" label="Reason">
            <Select placeholder="Select reason">
              <Option value="duplicate">Duplicate payment</Option>
              <Option value="fraudulent">Fraudulent</Option>
              <Option value="requested_by_customer">Requested by customer</Option>
              <Option value="product_not_received">Product not received</Option>
              <Option value="other">Other</Option>
            </Select>
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => setRefundModal({ visible: false, transaction: null })}>
                Cancel
              </Button>
              <Button type="primary" danger htmlType="submit">
                Process Refund
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TransactionList;
